import { createSignal, For, onCleanup, onMount } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import {
  type TabRecord,
  type ViewName,
  viewComponents,
} from "../core/views";
import { installRealtimeSession } from "../core/realtime";

let nextTabId = 2;

export function AppShell() {
  const [tabs, setTabs] = createSignal<TabRecord[]>([
    { id: 1, name: "Dashboard", title: "Dashboard" },
  ]);
  const [activeTabId, setActiveTabId] = createSignal(1);
  const activeTab = () =>
    tabs().find((tab) => tab.id === activeTabId()) ?? tabs()[0]!;

  onMount(() => {
    const cleanup = installRealtimeSession();
    onCleanup(cleanup);
  });

  const openView = (name: ViewName) => {
    setTabs((current) =>
      current.map((tab) =>
        tab.id === activeTabId() ? { ...tab, name, title: name } : tab,
      ),
    );
  };

  const newTab = () => {
    const tab = {
      id: nextTabId++,
      name: "Dashboard" as const,
      title: "Dashboard",
    };
    setTabs((current) => [...current, tab]);
    setActiveTabId(tab.id);
  };

  const closeTab = (id: number) => {
    const current = tabs();
    if (current.length === 1) {
      return;
    }
    const index = current.findIndex((tab) => tab.id === id);
    const remaining = current.filter((tab) => tab.id !== id);
    setTabs(remaining);
    if (activeTabId() === id) {
      setActiveTabId(remaining[Math.max(0, index - 1)]!.id);
    }
  };

  return (
    <main class="dump-root flex h-screen w-full items-center justify-center px-14">
      <div class="relative flex h-[92%] w-[98%] flex-col gap-[1.5rem] lg:flex-row">
        <Sidebar activeView={activeTab().name} onSelect={openView} />
        <section class="h-full w-full">
          <Topbar
            tabs={tabs()}
            activeTabId={activeTabId()}
            onSelect={setActiveTabId}
            onClose={closeTab}
            onNew={newTab}
            onSettings={() => openView("Config")}
          />
          <div class="mt-4 h-[calc(100%-2rem)] w-full gap-1">
            <For each={tabs()}>
              {(tab) => (
                <section
                  class="h-full w-full"
                  hidden={tab.id !== activeTabId()}
                  aria-label={tab.title}
                >
                  <Dynamic component={viewComponents[tab.name]} />
                </section>
              )}
            </For>
          </div>
        </section>
      </div>
    </main>
  );
}
